import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Appointments.css';

const API_URL = window.location.origin;

function Appointments({ token }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/calendar/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data.appointments || []);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    
    try {
      await axios.delete(`${API_URL}/calendar/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Appointment cancelled successfully');
      fetchAppointments();
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
      alert('Failed to cancel appointment');
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'status-confirmed';
      case 'cancelled': return 'status-cancelled';
      case 'completed': return 'status-completed';
      default: return 'status-pending';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  const upcomingAppointments = appointments.filter(apt => 
    new Date(apt.start_time) >= new Date() && apt.status !== 'cancelled'
  );
  
  const pastAppointments = appointments.filter(apt => 
    new Date(apt.start_time) < new Date() || apt.status === 'cancelled'
  );

  if (loading) {
    return (
      <div className="appointments-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-container">
      {error && <div className="error-banner">{error}</div>}
      
      <div className="appointments-header">
        <h2>Appointments</h2>
        <button className="refresh-btn" onClick={fetchAppointments}>
          Refresh
        </button>
      </div>

      <div className="appointments-stats">
        <div className="stat-box">
          <span className="stat-number">{upcomingAppointments.length}</span>
          <span className="stat-label">Upcoming</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{appointments.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{pastAppointments.length}</span>
          <span className="stat-label">Past</span>
        </div>
      </div>

      {appointments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"></div>
          <h3>No appointments yet</h3>
          <p>Appointments will appear here once customers book through your widget</p>
        </div>
      ) : (
        <>
          {upcomingAppointments.length > 0 && (
            <div className="appointments-section">
              <h3 className="section-title">Upcoming Appointments</h3>
              <div className="appointments-grid">
                {upcomingAppointments.map(apt => {
                  const { date, time } = formatDateTime(apt.start_time);
                  return (
                    <div key={apt.id} className="appointment-card">
                      <div className="appointment-header">
                        <div className="appointment-date">
                          <span className="date-day">{date.split(' ')[1]}</span>
                          <span className="date-month">{date.split(' ')[0]}</span>
                        </div>
                        <span className={`status-badge ${getStatusClass(apt.status)}`}>
                          {apt.status || 'Pending'}
                        </span>
                      </div>
                      <div className="appointment-body">
                        <h4>{apt.lead_name || 'No name provided'}</h4>
                        <div className="appointment-details">
                          <p>{time}</p>
                          {apt.lead_phone && <p>{apt.lead_phone}</p>}
                          {apt.lead_email && <p>{apt.lead_email}</p>}
                          {apt.notes && <p className="notes">{apt.notes}</p>}
                        </div>
                      </div>
                      <div className="appointment-actions">
                        <button 
                          className="btn-view"
                          onClick={() => setSelectedAppointment(apt)}
                        >
                          View Details
                        </button>
                        <button 
                          className="btn-cancel"
                          onClick={() => cancelAppointment(apt.id)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pastAppointments.length > 0 && (
            <div className="appointments-section">
              <h3 className="section-title">Past Appointments</h3>
              <div className="appointments-list">
                {pastAppointments.map(apt => {
                  const { date, time } = formatDateTime(apt.start_time);
                  return (
                    <div key={apt.id} className="appointment-row">
                      <div className="appointment-info">
                        <span className="appointment-name">{apt.lead_name || 'No name'}</span>
                        <span className="appointment-datetime">{date} at {time}</span>
                      </div>
                      <span className={`status-badge ${getStatusClass(apt.status)}`}>
                        {apt.status || 'Completed'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {selectedAppointment && (
        <div className="modal-overlay" onClick={() => setSelectedAppointment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Details</h3>
              <button className="modal-close" onClick={() => setSelectedAppointment(null)}>X</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Name:</strong>
                <span>{selectedAppointment.lead_name || 'Not provided'}</span>
              </div>
              <div className="detail-row">
                <strong>Date & Time:</strong>
                <span>
                  {formatDateTime(selectedAppointment.start_time).date} at {formatDateTime(selectedAppointment.start_time).time}
                </span>
              </div>
              {selectedAppointment.lead_phone && (
                <div className="detail-row">
                  <strong>Phone:</strong>
                  <span>{selectedAppointment.lead_phone}</span>
                </div>
              )}
              {selectedAppointment.lead_email && (
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>{selectedAppointment.lead_email}</span>
                </div>
              )}
              {selectedAppointment.notes && (
                <div className="detail-row">
                  <strong>Notes:</strong>
                  <span>{selectedAppointment.notes}</span>
                </div>
              )}
              <div className="detail-row">
                <strong>Status:</strong>
                <span className={`status-badge ${getStatusClass(selectedAppointment.status)}`}>
                  {selectedAppointment.status || 'Pending'}
                </span>
              </div>
              {selectedAppointment.google_event_id && (
                <div className="detail-row">
                  <strong>Google Calendar:</strong>
                  <span>Synced</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Appointments;
